import { defineStore } from 'pinia'
import { ref } from 'vue'
import { loadInstructions as apiLoadInstructions } from '@/backend/darwin/loadInstructions'
import { createInstruction as apiCreateInstruction } from '@/backend/darwin/createInstruction'
import { updateInstruction as apiUpdateInstruction } from '@/backend/darwin/updateInstruction'
import { deleteInstruction as apiDeleteInstruction } from '@/backend/darwin/deleteInstruction'
import { createInstructionVersion as apiCreateInstructionVersion } from '@/backend/darwin/createInstructionVersion'
import { activateInstructionVersion as apiActivateInstructionVersion } from '@/backend/darwin/activateInstructionVersion'
import { testInstruction as apiTestInstruction } from '@/backend/darwin/testInstruction'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useToast } from '@/uiKit/Toast/useToast'
import { computed } from 'vue'
import { type UUID } from '@/backend/schema/uuid'
import { type InstructionStatus, INSTRUCTION_STATUS } from './types'
import { type Instruction } from '@/backend/schema/instruction'
import { type InstructionVersion } from '@/backend/schema/instructionVersion'
import type {
  InstructionVersionProcessedPayload,
  InstructionFeedbackGeneratedPayload,
} from './useInstructionsChannelSubscriber'
import { mockWebSocketAiReviewFeedbackMessage } from './instructionsStoreMocks'

export type InstructionListItem = Pick<Instruction, 'id' | 'title'> &
  Pick<Instruction['activeVersion'], 'content' | 'insertedAt'> & {
    status: InstructionStatus
  }

const getCreateInstructionErrorMessage = (status: unknown): string => {
  if (status === 422) {
    return 'Rule with this title already exists'
  }
  if (status === 429) {
    return 'Too many rules are being processed. Wait for them to finish and try again.'
  }
  return 'Failed to create rule'
}

export const useInstructionsStore = defineStore('Instructions', () => {
  const teamStore = useTeamStore()
  const toast = useToast()

  const teamSlug = computed(() => teamStore.currentTeam?.slug ?? '')

  const instructions = ref<Instruction[]>([])
  const hasLoadedInstructions = ref(false)

  const instructionTestingFeedback = ref<{
    summary: string
    items: {
      criteriaMet: boolean
      criteriaText: string
      comment: string | null
    }[]
  } | null>(null)
  const instructionIdBeingTested = ref<UUID | null>(null)

  const loadInstructions = async (): Promise<void> => {
    const response = await apiLoadInstructions({
      team_slug: teamSlug.value,
    })

    if ('error' in response) {
      return
    }

    instructions.value = response.data.instructions
    hasLoadedInstructions.value = true
  }

  const createInstruction = async (title: string, content: string): Promise<UUID | undefined> => {
    const response = await apiCreateInstruction({
      team_slug: teamSlug.value,
      title,
      content,
      multi_modal: false,
    })

    if ('error' in response) {
      toast.warning({ meta: { title: getCreateInstructionErrorMessage(response.error.status) } })
      return
    }

    instructions.value.push(response.data)

    return response.data.id
  }

  const updateInstructionTitle = async (instructionId: UUID, title: string): Promise<void> => {
    const response = await apiUpdateInstruction({
      team_slug: teamSlug.value,
      id: instructionId,
      title,
    })

    if ('error' in response) {
      toast.warning({
        meta: { title: 'Failed to update rule title' },
      })
      return
    }

    const instruction = instructions.value.find((instruction) => instruction.id === instructionId)
    if (!instruction) {
      return
    }

    instruction.title = response.data.title
  }

  const updateInstructionContent = async (
    instructionId: UUID,
    content: string,
  ): Promise<InstructionVersion | undefined> => {
    const response = await apiCreateInstructionVersion({
      team_slug: teamSlug.value,
      id: instructionId,
      content,
      multi_modal: false,
      active: true,
    })

    if ('error' in response) {
      toast.warning({
        meta: { title: 'Failed to update rule prompt' },
      })
      return
    }

    const instruction = instructions.value.find((instruction) => instruction.id === instructionId)
    if (!instruction) {
      return
    }

    instruction.activeVersion = response.data

    return response.data
  }

  const deleteInstruction = async (instructionId: UUID): Promise<boolean> => {
    const response = await apiDeleteInstruction({
      team_slug: teamSlug.value,
      id: instructionId,
    })

    if ('error' in response) {
      toast.warning({
        meta: { title: 'Failed to delete rule' },
      })
      return false
    }

    instructions.value = instructions.value.filter(
      (instruction) => instruction.id !== instructionId,
    )

    return true
  }

  const activateInstructionVersion = async (
    instructionId: UUID,
    version: InstructionVersion,
  ): Promise<void> => {
    const response = await apiActivateInstructionVersion({
      team_slug: teamSlug.value,
      instruction_id: instructionId,
      version_id: version.id,
    })

    if ('error' in response) {
      toast.warning({
        meta: { title: 'Failed to activate rule version' },
      })
      return
    }

    const instruction = instructions.value.find((instruction) => instruction.id === instructionId)
    if (!instruction) {
      return
    }

    instruction.activeVersion = version
  }

  const testInstruction = async (
    instructionId: UUID,
    instructionVersionId: UUID,
    content: string,
    useMockResponse: boolean,
  ): Promise<boolean> => {
    const response = await apiTestInstruction(
      {
        team_slug: teamSlug.value,
        instruction_id: instructionId,
        instruction_version_id: instructionVersionId,
        content,
      },
      { useMockResponse },
    )

    if ('error' in response) {
      toast.warning({
        meta: { title: 'Failed to test rule' },
      })
      return false
    }

    return true
  }

  const completeInstructionTesting = (payload: InstructionFeedbackGeneratedPayload): void => {
    if (instructionIdBeingTested.value === payload.instruction_id) {
      instructionTestingFeedback.value = {
        summary: payload.feedback.summary,
        items: payload.feedback.items.map((item) => ({
          criteriaMet: item.criteria_met,
          criteriaText: item.criteria_text,
          comment: item.comment,
        })),
      }
    }

    instructionIdBeingTested.value = null
  }

  const beginInstructionTesting = async (
    instructionId: UUID,
    instructionVersionId: UUID,
    content: string,
    useMockResponse: boolean = false,
  ): Promise<void> => {
    instructionTestingFeedback.value = null
    instructionIdBeingTested.value = instructionId

    const result = await testInstruction(
      instructionId,
      instructionVersionId,
      content,
      useMockResponse,
    )

    if (!result) {
      instructionIdBeingTested.value = null
    }

    if (useMockResponse) {
      completeInstructionTesting(
        mockWebSocketAiReviewFeedbackMessage(instructionId, instructionVersionId),
      )
    }
  }

  const resetInstructionTesting = (): void => {
    instructionTestingFeedback.value = null
    instructionIdBeingTested.value = null
  }

  const getInstruction = (instructionId: UUID): Instruction | undefined =>
    instructions.value.find((instruction) => instruction.id === instructionId)

  const getInstructionStatus = (instruction: Instruction): InstructionStatus => {
    if (instruction.activeVersion.isReadyForValidation) {
      return INSTRUCTION_STATUS.READY
    }

    if (instruction.activeVersion.hasProcessingError) {
      return INSTRUCTION_STATUS.FAILED
    }

    return INSTRUCTION_STATUS.PROCESSING
  }

  const mapInstructionToListItem = (instruction: Instruction): InstructionListItem => ({
    id: instruction.id as UUID,
    title: instruction.title,
    content: instruction.activeVersion.content,
    status: getInstructionStatus(instruction),
    insertedAt: instruction.activeVersion.insertedAt,
  })

  const getInstructions = computed<InstructionListItem[]>(() =>
    [...instructions.value]
      .sort((a, b) => (b.activeVersion.insertedAt > a.activeVersion.insertedAt ? 1 : -1))
      .map(mapInstructionToListItem),
  )

  const processInstructionVersion = (payload: InstructionVersionProcessedPayload): void => {
    const instruction = getInstruction(payload.instruction_id)
    if (!instruction) {
      return
    }

    if (instruction.activeVersion.id !== payload.instruction_version_id) {
      return
    }

    instruction.activeVersion.isReadyForValidation = payload.ready_for_validation
    instruction.activeVersion.hasProcessingError = payload.has_processing_error
  }

  const areAnyInstructionsProcessing = computed(
    () =>
      instructions.value.length > 0 &&
      instructions.value.some(
        (instruction) => getInstructionStatus(instruction) === INSTRUCTION_STATUS.PROCESSING,
      ),
  )

  const resetState = (): void => {
    instructions.value = []
    hasLoadedInstructions.value = false
    instructionTestingFeedback.value = null
    instructionIdBeingTested.value = null
  }

  return {
    instructions: getInstructions,
    hasInstructions: computed(() => getInstructions.value.length > 0),
    loadInstructions,
    hasLoadedInstructions,
    createInstruction,
    updateInstructionTitle,
    updateInstructionContent,
    deleteInstruction,
    activateInstructionVersion,
    completeInstructionTesting,
    beginInstructionTesting,
    resetInstructionTesting,
    instructionTestingFeedback: computed(() => instructionTestingFeedback.value),
    isTestingInstruction: computed(() => instructionIdBeingTested.value !== null),
    processInstructionVersion,
    getInstruction,
    getInstructionStatus,
    areAnyInstructionsProcessing,
    resetState,
  }
})
